import { Box, Button, Heading, HStack, Text, VStack } from "@chakra-ui/react";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { LuHome, LuRefreshCw } from "react-icons/lu";
import {
  Link as RouterLink,
  isRouteErrorResponse,
  useRouteError,
} from "react-router-dom";

interface ErrorStateProps {
  code?: string;
  title: string;
  description: string;
  showReload?: boolean;
}

const ErrorState = ({
  code,
  title,
  description,
  showReload = false,
}: ErrorStateProps) => (
  <Box
    as="main"
    minH="100dvh"
    display="flex"
    alignItems="center"
    justifyContent="center"
    px={4}
    bg="bg.canvas"
  >
    <VStack
      gap={3}
      textAlign="center"
      maxW="md"
      bg="bg.panel"
      borderWidth="1px"
      borderColor="border"
      rounded="xl"
      shadow="xs"
      p={{ base: 6, md: 10 }}
    >
      {code && (
        <Text fontSize="sm" fontWeight="semibold" color="fg.muted">
          {code}
        </Text>
      )}
      <Heading as="h1" size="xl" letterSpacing="tight" color="fg">
        {title}
      </Heading>
      <Text color="fg.muted">{description}</Text>
      <HStack gap={3} pt={3} flexWrap="wrap" justify="center">
        <Button colorPalette="brand" asChild>
          <RouterLink to="/">
            <LuHome />
            Volver al inicio
          </RouterLink>
        </Button>
        {showReload && (
          <Button variant="outline" onClick={() => window.location.reload()}>
            <LuRefreshCw />
            Recargar
          </Button>
        )}
      </HStack>
    </VStack>
  </Box>
);

export const NotFoundPage = () => (
  <ErrorState
    code="Error 404"
    title="Página no encontrada"
    description="La dirección que abriste no existe o fue movida."
  />
);

export const RouteErrorPage = () => {
  const error = useRouteError();

  useEffect(() => {
    if (!isRouteErrorResponse(error)) Sentry.captureException(error);
  }, [error]);

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />;
  }

  return (
    <ErrorState
      code="Algo salió mal"
      title="No pudimos mostrar esta pantalla"
      description="Ocurrió un error inesperado. Recarga la página o vuelve al inicio; si se repite, avisa al administrador."
      showReload
    />
  );
};
